import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Send,
  Paperclip,
  Trash2,
  X,
  Settings as SettingsIcon,
  FileText,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import type {
  ProjectChatDto,
  ProjectChatModel,
  ProjectChatContextFlags,
} from '@cc/types';

const MODEL_OPTIONS: Array<{ value: ProjectChatModel; label: string }> = [
  { value: 'claude-opus-4-7', label: 'Opus 4.7 (best, slowest)' },
  { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6 (default)' },
  { value: 'claude-haiku-4-5', label: 'Haiku 4.5 (fast)' },
];

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const payload = result.includes(',') ? result.slice(result.indexOf(',') + 1) : result;
      resolve(payload);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

export function ProjectChatPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { data: chat, isLoading, error } = useQuery({
    queryKey: qk.projectChat(id ?? ''),
    queryFn: () => api.getProjectChat(id!),
    enabled: Boolean(id),
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [chat?.messages.length]);

  const send = useMutation({
    mutationFn: (message: string) => api.sendProjectChat(id!, { message, source: 'WEB' }),
    onSuccess: (res) => {
      queryClient.setQueryData<ProjectChatDto | undefined>(
        qk.projectChat(id!),
        (old) =>
          old
            ? {
                ...old,
                messages: [...old.messages, res.userMessage, res.assistantMessage],
              }
            : old,
      );
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Chat failed.';
      toast.error(msg);
    },
  });

  const patch = useMutation({
    mutationFn: (body: {
      systemPrompt?: string;
      model?: ProjectChatModel;
      contextFlags?: Partial<ProjectChatContextFlags>;
    }) => api.patchProjectChat(id!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projectChat(id!) });
    },
    onError: () => {
      toast.error('Failed to save settings.');
    },
  });

  const clearThread = useMutation({
    mutationFn: () => api.clearProjectChat(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projectChat(id!) });
      toast.success('Conversation cleared.');
    },
    onError: () => {
      toast.error('Failed to clear thread.');
    },
  });

  const uploadAttachment = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        throw new Error(`${file.name} is over the 10 MB limit.`);
      }
      const contentBase64 = await fileToBase64(file);
      return api.uploadProjectChatAttachment(id!, {
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        contentBase64,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projectChat(id!) });
      toast.success('Attachment added.');
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Upload failed.';
      toast.error(msg);
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: (attachmentId: string) => api.deleteProjectChatAttachment(id!, attachmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projectChat(id!) });
    },
    onError: () => {
      toast.error('Failed to delete attachment.');
    },
  });

  if (!id) return <p>Bad route.</p>;
  if (error) {
    const code = error instanceof ApiError ? error.code : 'unknown';
    return (
      <div className="space-y-3">
        <p className="text-sm text-destructive">Failed to load chat ({code}).</p>
        {code === 'claude_oauth_not_configured' && (
          <p className="text-xs text-muted-foreground">
            Run <code className="rounded bg-muted px-1">claude setup-token</code> locally and set
            <code className="ml-1 rounded bg-muted px-1">CLAUDE_CODE_OAUTH_TOKEN</code> on the API.
          </p>
        )}
      </div>
    );
  }
  if (isLoading || !chat) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const submit = () => {
    const text = input.trim();
    if (!text || send.isPending) return;
    setInput('');
    send.mutate(text);
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to={`/projects/${id}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> {chat.projectName}
          </Link>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">
            {chat.title || chat.projectName} chat
          </h1>
          <p className="text-xs text-muted-foreground">
            updated {relativeTime(chat.updatedAt)} · model {chat.model}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSettingsOpen((v) => !v)}
            title="Settings"
          >
            <SettingsIcon className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">Settings</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm('Clear this conversation? Attachments and settings are kept.')) {
                clearThread.mutate();
              }
            }}
            disabled={clearThread.isPending || chat.messages.length === 0}
          >
            <Trash2 className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">Clear</span>
          </Button>
        </div>
      </header>

      <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-3">
        <section className={cn('lg:col-span-2', settingsOpen ? 'hidden lg:block' : '')}>
          <ChatPane
            chat={chat}
            scrollRef={scrollRef}
            input={input}
            setInput={setInput}
            submit={submit}
            isSending={send.isPending}
          />
        </section>
        <aside className={cn(settingsOpen ? 'block' : 'hidden lg:block')}>
          <SettingsPane
            chat={chat}
            onPatch={(body) => patch.mutate(body)}
            onUploadFiles={(files) => {
              for (const f of files) uploadAttachment.mutate(f);
            }}
            onDeleteAttachment={(attachmentId) => deleteAttachment.mutate(attachmentId)}
            isPatching={patch.isPending}
            isUploading={uploadAttachment.isPending}
          />
        </aside>
      </div>
    </div>
  );
}

interface ChatPaneProps {
  chat: ProjectChatDto;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  input: string;
  setInput: (v: string) => void;
  submit: () => void;
  isSending: boolean;
}

function ChatPane({ chat, scrollRef, input, setInput, submit, isSending }: ChatPaneProps) {
  return (
    <Card className="flex h-full flex-col">
      <CardContent className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-md border border-border bg-background/40 p-3"
        >
          {chat.messages.length === 0 ? (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3" /> Ask anything about {chat.projectName}. I have the
                project notes, recent activity, and any files you've uploaded as context.
              </p>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {chat.messages.map((m) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 140, damping: 16 }}
                  className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}
                >
                  <div
                    className={cn(
                      'max-w-[85%] rounded-md px-3 py-2 text-sm',
                      m.role === 'user'
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-muted text-foreground',
                    )}
                  >
                    <p className="whitespace-pre-wrap">{m.contentMd}</p>
                    {m.source !== 'WEB' && (
                      <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                        via {m.source.toLowerCase()}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
          {isSending && (
            <div className="flex justify-start">
              <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="animate-pulse">thinking</span>
                  <span className="animate-pulse delay-100">.</span>
                  <span className="animate-pulse delay-200">.</span>
                  <span className="animate-pulse delay-300">.</span>
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Message ${chat.projectName}...`}
            className="min-h-[44px] flex-1 resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            disabled={isSending}
          />
          <Button size="sm" onClick={submit} disabled={isSending || !input.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface SettingsPaneProps {
  chat: ProjectChatDto;
  onPatch: (body: {
    systemPrompt?: string;
    model?: ProjectChatModel;
    contextFlags?: Partial<ProjectChatContextFlags>;
  }) => void;
  onUploadFiles: (files: File[]) => void;
  onDeleteAttachment: (attachmentId: string) => void;
  isPatching: boolean;
  isUploading: boolean;
}

function SettingsPane({
  chat,
  onPatch,
  onUploadFiles,
  onDeleteAttachment,
  isPatching,
  isUploading,
}: SettingsPaneProps) {
  const [systemPrompt, setSystemPrompt] = useState(chat.systemPrompt);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Re-sync if server-side value changes (e.g. another tab edited it).
  useEffect(() => {
    setSystemPrompt(chat.systemPrompt);
  }, [chat.systemPrompt]);

  const flags = chat.contextFlags;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Model
          </h2>
          <Select
            value={chat.model}
            onValueChange={(v) => onPatch({ model: v as ProjectChatModel })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODEL_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            Billed against your Max plan via OAuth, not pay-as-you-go API.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Project instructions
          </h2>
          <Textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="What does this project need? Tone, constraints, who's involved..."
            className="min-h-[140px] resize-none text-sm"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              disabled={isPatching || systemPrompt === chat.systemPrompt}
              onClick={() => onPatch({ systemPrompt })}
            >
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Live context
          </h2>
          <div className="space-y-2 text-sm">
            <ContextToggle
              label="Recent activity (last 30 days)"
              checked={flags.activities}
              onChange={(v) => onPatch({ contextFlags: { activities: v } })}
            />
            <ContextToggle
              label="Upcoming calendar (next 14 days)"
              checked={flags.calendar}
              onChange={(v) => onPatch({ contextFlags: { calendar: v } })}
            />
            <ContextToggle
              label="Project emails (last 10)"
              checked={flags.emails}
              onChange={(v) => onPatch({ contextFlags: { emails: v } })}
            />
            <ContextToggle
              label="Open drafts (last 10)"
              checked={flags.drafts}
              onChange={(v) => onPatch({ contextFlags: { drafts: v } })}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Toggles control which live blocks get sent on every turn. Heavier context costs more
            tokens.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Attached files
            </h2>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  onUploadFiles(Array.from(e.target.files));
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }
              }}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-3.5 w-3.5" />
              <span className="ml-1">Add</span>
            </Button>
          </div>
          {chat.attachments.length === 0 ? (
            <p className="text-xs italic text-muted-foreground">No files attached.</p>
          ) : (
            <ul className="space-y-1.5">
              {chat.attachments.map((att) => (
                <li
                  key={att.id}
                  className="flex items-center gap-2 rounded-md border border-border bg-background/40 px-2 py-1.5 text-xs"
                >
                  <FileText className="h-3 w-3 flex-none text-muted-foreground" />
                  <span className="flex-1 truncate" title={att.filename}>
                    {att.filename}
                  </span>
                  <span className="flex-none text-[10px] text-muted-foreground">
                    {Math.round(att.sizeBytes / 1024)} KB
                  </span>
                  <button
                    onClick={() => onDeleteAttachment(att.id)}
                    className="text-muted-foreground hover:text-destructive"
                    title="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-muted-foreground">
            Files live in your Command Centre Media folder on Drive. Text/markdown is inlined as
            context; PDFs are referenced.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ContextToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-2">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 cursor-pointer"
      />
    </label>
  );
}
